import { useEffect, useId, useState } from 'react'

import { Label } from 'react-aria-components'
import { useFormContext, type FieldPath, type FieldValues, type PathValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Render } from '@/components/render-if'
import { Button, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { VagaPadraoAcessoriosExtras } from '@/features/proposta/components/vaga-padrao/vaga-padrao-acessorios-extras'
import { VagaPadraoSelector } from '@/features/proposta/components/vaga-padrao/vaga-padrao-seletor'
import type { TVagasSelecionadas } from '@/features/proposta/interfaces/proposta.type'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import type { TProduto } from '@/types/produto'
import { opcaoVagaEnum, type TOpcaoVagaEnum } from '@/types/proposta'

const Step = {
  SELECIONAR_OPCAO: 0,
  DEFINIR_VAGA: 1,
} as const

type TVagaPadraoMultiplosAcessoriosProps = {
  vagas: Array<{
    nome: string
    produtos: TProduto[]
  }>
  acessorios: Array<TPropostaSchemaInput['informacoesIniciais']['simulacoesAcessorios'][number]['produto']>
  onConfirm: (selectedVagas: TVagasSelecionadas) => void
}

export const VagaPadraoEscolhaOpcoes = ({ vagas, acessorios, onConfirm }: TVagaPadraoMultiplosAcessoriosProps) => {
  const id = useId()
  const { t } = useTranslation('proposta')
  const [step, setStep] = useState<(typeof Step)[keyof typeof Step]>(Step.SELECIONAR_OPCAO)
  const { watch, setValue } = useFormContext<FieldValues>()

  const opcaoVagaConfirmada = watch('informacoesIniciais.opcaoVaga' as FieldPath<FieldValues>) as
    | TOpcaoVagaEnum
    | null
    | undefined
  const defaultSelecionadas = watch('informacoesIniciais.vagaIds' as FieldPath<FieldValues>) as TVagasSelecionadas
  const [opcaoSelecionada, setOpcaoSelecionada] = useState<TOpcaoVagaEnum | null | undefined>(opcaoVagaConfirmada)

  const selecionouEscolherVagaPadrao = opcaoSelecionada === opcaoVagaEnum.Padrao
  const selecionouTrocarVagaPorAcessorio = opcaoSelecionada === opcaoVagaEnum.Customizada

  const handleTrocarConteudoNaConfirmacaoOpcao = () => {
    setStep(Step.DEFINIR_VAGA)
    setValue(
      'informacoesIniciais.opcaoVaga' as FieldPath<FieldValues>,
      opcaoSelecionada as PathValue<FieldValues, FieldPath<FieldValues>>,
    )
    const vagasSelecionadasSaoDeTroca = defaultSelecionadas.some((vaga) => !!vaga.substituiVagaPadrao)

    const escolhaIncompativelComVagasSelecionadas =
      (selecionouEscolherVagaPadrao && vagasSelecionadasSaoDeTroca) ||
      (selecionouTrocarVagaPorAcessorio && !vagasSelecionadasSaoDeTroca)

    if (escolhaIncompativelComVagasSelecionadas) {
      setValue(
        'informacoesIniciais.vagaIds' as FieldPath<FieldValues>,
        [] as PathValue<FieldValues, FieldPath<FieldValues>>,
      )
    }
  }

  const handleGoBack = () => {
    setStep(Step.SELECIONAR_OPCAO)
  }

  const renderizarDeAcordoComOpcaoSelecionada = () => {
    if (selecionouEscolherVagaPadrao) {
      return <VagaPadraoSelector handleGoBack={handleGoBack} vagas={vagas} onConfirm={onConfirm} />
    }
    return (
      <VagaPadraoAcessoriosExtras
        acessorios={acessorios}
        onConfirmar={onConfirm}
        handleGoBack={handleGoBack}
        defaultSelecionadas={defaultSelecionadas}
      />
    )
  }

  useEffect(() => {
    const apenasUmAcessorioExtraNaProposta =
      step === Step.DEFINIR_VAGA && selecionouTrocarVagaPorAcessorio && acessorios.length === 1

    if (apenasUmAcessorioExtraNaProposta) {
      onConfirm([
        {
          id: acessorios[0]!.idExterno,
          nomeVaga: 'Acessorio',
          descricao: acessorios[0]!.codigo,
          substituiVagaPadrao: true,
        },
      ])
    }
  }, [step, acessorios, onConfirm, selecionouTrocarVagaPorAcessorio])

  return (
    <Render.If condition={step === Step.SELECIONAR_OPCAO} fallback={renderizarDeAcordoComOpcaoSelecionada()}>
      {() => (
        <>
          <SheetHeader className="box-border max-w-137 gap-2 p-0 pt-16">
            <SheetTitle className="text-2xl font-normal text-neutral-700 uppercase">
              {t('informacoes_iniciais.cards.vaga_padrao.escolha_opcoes.titulo')}
            </SheetTitle>
            <SheetDescription className="text-base text-neutral-500">
              {t('informacoes_iniciais.cards.vaga_padrao.escolha_opcoes.descricao')}
            </SheetDescription>
          </SheetHeader>
          <RadioGroup
            value={opcaoSelecionada ?? ''}
            onValueChange={(value: TOpcaoVagaEnum) => setOpcaoSelecionada(value)}
            className="font-nunito flex flex-col gap-6"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value={opcaoVagaEnum.Padrao} id={`${id}-${opcaoVagaEnum.Padrao}`} />
              <Label className="text-neutral-700" htmlFor={`${id}-${opcaoVagaEnum.Padrao}`}>
                {t('informacoes_iniciais.cards.vaga_padrao.escolha_opcoes.escolher_vaga_padrao')}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value={opcaoVagaEnum.Customizada} id={`${id}-${opcaoVagaEnum.Customizada}`} />
              <Label className="text-neutral-700" htmlFor={`${id}-${opcaoVagaEnum.Customizada}`}>
                {t('informacoes_iniciais.cards.vaga_padrao.escolha_opcoes.troca_por_acessorio')}
              </Label>
            </div>
          </RadioGroup>
          <SheetFooter className="fixed right-0 bottom-0 left-0 bg-white p-4 shadow-[0_-2px_10px_0_rgba(0,0,0,0.1)] sm:relative sm:grid sm:p-0 sm:py-4 sm:shadow-none">
            <Button
              disabled={opcaoSelecionada === opcaoVagaEnum.Pendente}
              className="w-fit w-full justify-self-end aria-[disabled=true]:pointer-events-none sm:w-fit"
              onClick={handleTrocarConteudoNaConfirmacaoOpcao}
            >
              {t('informacoes_iniciais.cards.vaga_padrao.trigger.confirmar')}
            </Button>
          </SheetFooter>
        </>
      )}
    </Render.If>
  )
}
